import { ConfigUnsponsored, CustomAxiosError } from '@calm/evexia-web-sdk';
import createCircleApi from '@rhg/libs/api/create-circle-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryClient,
    queryKeys,
    useMutation,
    useQuery,
} from '@rhg/libs/react-query';
import { AxiosError } from 'axios';
import { useActiveCircle } from '../use-active-circle';

const unsponsoredConfigQueryOptions = {
    queryKey: () => {
        return [queryKeys.unsponsoredConfig];
    },
    queryFn: async ({
        getAccessTokenSilently,
    }: {
        getAccessTokenSilently: () => Promise<string>;
    }) => {
        const circleApi = createCircleApi(getAccessTokenSilently);

        const response = await circleApi
            .getCircleUnsponsoredConfig({})
            .catch((e: CustomAxiosError) => {
                if (e?.response?.status === 404) {
                    return { data: null };
                } else {
                    throw e;
                }
            });
        return response.data;
    },
    queryOptions: ({
        isAuthenticated,
        isSponsored,
    }: {
        isAuthenticated: boolean;
        isSponsored: boolean;
    }) => {
        return {
            retry: false,
            enabled: Boolean(isAuthenticated && !isSponsored),
        };
    },
};

export function useUnsponsoredConfig() {
    const { activeCircle } = useActiveCircle();
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const isSponsored = Boolean(activeCircle?.sponsor);
    const { queryKey, queryFn, queryOptions } = unsponsoredConfigQueryOptions;
    const options = queryOptions({
        isAuthenticated,
        isSponsored,
    });

    const { data, error, ...otherProps } = useQuery<
        ConfigUnsponsored | null | undefined,
        AxiosError
    >({
        queryKey: queryKey(),
        queryFn: async () =>
            queryFn({
                getAccessTokenSilently,
            }),
        ...options,
    });

    return {
        unsponsoredConfig: data,
        error,
        ...otherProps,
    };
}

export function useGetUnsponsoredConfig() {
    const { queryKey, queryFn } = unsponsoredConfigQueryOptions;
    const { activeCircle } = useActiveCircle();
    const isSponsored = Boolean(activeCircle?.sponsor);
    const { getAccessTokenSilently } = useAuth();
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async () => {
            if (isSponsored) {
                return null;
            }
            const response = await queryFn({
                getAccessTokenSilently,
            });
            queryClient.setQueryData<ConfigUnsponsored | null>(
                queryKey(),
                response
            );

            return response;
        },
    });

    return {
        getUnsponsoredConfig: mutate,
        getUnsponsoredConfigAsync: mutateAsync,
        ...otherProps,
    };
}
